import React, { useMemo, useState } from 'react';

import { DateTime } from 'luxon';

import { AnchorButton as Link, Table, Text } from '@shared/components/bootstrap';

import { Label } from '@admin/components/helpers/label';
import { Panel } from '@admin/components/helpers/panel';
import { Spinner } from '@admin/components/spinner';
import { client } from '@admin/libraries/apollo';
import { UserSkillsReportQuery, useUserSkillsReportQuery } from '@admin/schema';

const ALL = '';

type Assignment = UserSkillsReportQuery['userSkills'][0];
type Training = UserSkillsReportQuery['userSkillTrainings'][0];
type CatalogSkill = UserSkillsReportQuery['skillCatalog'][0];
type RowStatus = 'ACTIVE' | 'REVOKED' | 'IN_TRAINING';

type LabelKind = 'success' | 'muted' | 'info';

interface ReportRow {
  key: string;
  user: Assignment['user'];
  skill: Assignment['skill'];
  status: RowStatus;
  createdAt?: string | null;
  revokedAt?: string | null;
}

const STATUS_LABEL: Record<RowStatus, LabelKind> = {
  ACTIVE: 'success',
  REVOKED: 'muted',
  IN_TRAINING: 'info',
};

const STATUS_SORT_RANK: Record<RowStatus, number> = {
  ACTIVE: 3,
  IN_TRAINING: 2,
  REVOKED: 1,
};

const STATUS_FILTER_OPTIONS: { value: RowStatus; label: string }[] = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'IN_TRAINING', label: 'In Training' },
  { value: 'REVOKED', label: 'Inactive' },
];

const formatDate = (iso: string | null | undefined) =>
  iso ? DateTime.fromISO(iso).toLocaleString(DateTime.DATE_MED) : '—';

const formatStatus = (status: RowStatus) => {
  if (status === 'IN_TRAINING') return 'In Training';
  return status.charAt(0) + status.slice(1).toLowerCase();
};

const rowKey = (userId: string, skillKind: string) => `${userId}:${skillKind}`;

const countByStatus = (rows: ReportRow[]) =>
  rows.reduce(
    (counts, row) => {
      counts[row.status] += 1;
      return counts;
    },
    { ACTIVE: 0, IN_TRAINING: 0, REVOKED: 0 },
  );

const buildReportRows = (assignments: Assignment[], trainings: Training[]): ReportRow[] => {
  const inProgressByKey = new Map(
    trainings
      .filter((training) => training.inProgress)
      .map((training) => [rowKey(training.user.id, training.skill.kind), training]),
  );
  const rows: ReportRow[] = [];

  assignments.forEach((assignment) => {
    const key = rowKey(assignment.user.id, assignment.skill.kind);
    if (inProgressByKey.has(key)) {
      rows.push({
        key: `training:${key}`,
        user: assignment.user,
        skill: assignment.skill,
        status: 'IN_TRAINING',
        createdAt: assignment.createdAt,
        revokedAt: assignment.revokedAt,
      });
      inProgressByKey.delete(key);
      return;
    }

    rows.push({
      key: `skill:${assignment.id}`,
      user: assignment.user,
      skill: assignment.skill,
      status: assignment.status as Exclude<RowStatus, 'IN_TRAINING'>,
      createdAt: assignment.createdAt,
      revokedAt: assignment.revokedAt,
    });
  });

  inProgressByKey.forEach((training, key) => {
    rows.push({
      key: `training:${key}`,
      user: training.user,
      skill: training.skill,
      status: 'IN_TRAINING',
    });
  });

  return rows.sort((left, right) => {
    const statusDiff = STATUS_SORT_RANK[right.status] - STATUS_SORT_RANK[left.status];
    if (statusDiff !== 0) return statusDiff;
    const nameDiff = left.user.name.localeCompare(right.user.name);
    if (nameDiff !== 0) return nameDiff;
    return left.skill.displayName.localeCompare(right.skill.displayName);
  });
};

const matchesFilters = (
  row: ReportRow,
  { nameSearch, skillFilter, statusFilter }: { nameSearch: string; skillFilter: string; statusFilter: string },
) => {
  const search = nameSearch.trim().toLowerCase();
  if (search && !row.user.name.toLowerCase().includes(search)) return false;
  if (skillFilter && row.skill.kind !== skillFilter) return false;
  if (statusFilter && row.status !== statusFilter) return false;
  return true;
};

export const UserSkillsReport: React.FC = () => {
  const [nameSearch, setNameSearch] = useState('');
  const [skillFilter, setSkillFilter] = useState(ALL);
  const [statusFilter, setStatusFilter] = useState(ALL);

  const { data, loading, error } = useUserSkillsReportQuery({ client });

  const catalog: CatalogSkill[] = data?.skillCatalog ?? [];
  const rows = useMemo(
    () => buildReportRows(data?.userSkills ?? [], data?.userSkillTrainings ?? []),
    [data?.userSkills, data?.userSkillTrainings],
  );

  const filteredRows = useMemo(
    () => rows.filter((row) => matchesFilters(row, { nameSearch, skillFilter, statusFilter })),
    [rows, nameSearch, skillFilter, statusFilter],
  );
  const stats = useMemo(() => countByStatus(filteredRows), [filteredRows]);

  return (
    <>
      <Panel>
        <Panel.Header>
          <Panel.Title>Qualifications Catalog</Panel.Title>
        </Panel.Header>
        <Panel.Body>
          <Text tag="p" style="muted">
            Reference list of qualifications that can be assigned to worker profiles.
          </Text>

          <Table responsive striped>
            <thead>
              <tr>
                <th>Display Name</th>
                <th>Kind</th>
              </tr>
            </thead>
            <tbody>
              {catalog.map((skill) => (
                <tr key={skill.id}>
                  <td>{skill.displayName}</td>
                  <td>
                    <code>{skill.kind}</code>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Panel.Body>
      </Panel>

      <Panel>
        <Panel.Header>
          <Panel.Title>Worker Qualifications</Panel.Title>
        </Panel.Header>
        <Panel.Body>
          <div className="row" style={{ marginBottom: 16 }}>
            <div className="col-sm-4">
              <input
                type="search"
                className="form-control"
                placeholder="Search by worker name"
                aria-label="Worker name search"
                value={nameSearch}
                onChange={(event) => setNameSearch(event.target.value)}
              />
            </div>
            <div className="col-sm-3">
              <select
                className="form-control"
                aria-label="Qualification filter"
                value={skillFilter}
                onChange={(event) => setSkillFilter(event.target.value)}
              >
                <option value={ALL}>All Qualifications</option>
                {catalog.map((skill) => (
                  <option key={skill.id} value={skill.kind}>
                    {skill.displayName}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-sm-3">
              <select
                className="form-control"
                aria-label="Status filter"
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
              >
                <option value={ALL}>All Statuses</option>
                {STATUS_FILTER_OPTIONS.map(({ value, label }) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <Text tag="p">
            Showing <strong>{filteredRows.length}</strong> of <strong>{rows.length}</strong> —{' '}
            <strong>{stats.ACTIVE}</strong> active, <strong>{stats.IN_TRAINING}</strong> in training,{' '}
            <strong>{stats.REVOKED}</strong> inactive.
          </Text>

          {error && (
            <Text tag="p" style="danger">
              {error.message}
            </Text>
          )}

          <Table responsive striped>
            <thead>
              <tr>
                <th>Worker</th>
                <th>Team</th>
                <th>Qualification</th>
                <th>Status</th>
                <th>Granted</th>
                <th>Inactive</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((row) => (
                <tr key={row.key}>
                  <td>
                    <Link kind="primary" href={`/users/${row.user.id}`}>
                      {row.user.name}
                    </Link>
                  </td>
                  <td>{row.user.team?.name ?? '—'}</td>
                  <td>{row.skill.displayName}</td>
                  <td>
                    <Label kind={STATUS_LABEL[row.status]}>{formatStatus(row.status)}</Label>
                  </td>
                  <td>{formatDate(row.createdAt)}</td>
                  <td>{formatDate(row.revokedAt)}</td>
                </tr>
              ))}
            </tbody>
          </Table>

          {loading && <Spinner />}
        </Panel.Body>
      </Panel>
    </>
  );
};
